import applianceData from '../data/generator_appliances.json';

const MARGIN_PCT = applianceData.defaults.margin_pct;

const electricDataset = applianceData.datasets.find(d => d.fuel_type === 'electric');
const propaneDataset = applianceData.datasets.find(d => d.fuel_type === 'propane');

// Map UI selections to appliance IDs in each dataset
export const comparisonApplianceMap = {
  kitchenRange: {
    electric: 'electric_oven_or_range',
    propane: 'propane_range_controls',
  },
  clothesDryer: {
    electric: 'electric_dryer',
    propane: 'propane_dryer_motor',
  },
  airFurnace: {
    electric: 'electric_furnace',
    propane: 'air_handler_and_pump',
  },
  waterHeater: {
    electric: 'electric_water_heater',
    propane: 'propane_water_heater_controls',
  },
};

// Electric appliances available in Section 2 (same IDs in both datasets)
export const electricAppliancesList = [
  { id: 'refrigerator', label: 'Refrigerator' },
  { id: 'lighting', label: 'Lighting' },
  { id: 'microwave', label: 'Microwave' },
  { id: 'central_a_c', label: 'Central A/C' },
  { id: 'basic_electronics', label: 'Basic Electronics' },
  { id: 'well_pump', label: 'Well Pump' },
  { id: 'garage_door', label: 'Garage Door' },
  { id: 'sump_pump', label: 'Sump Pump' },
  { id: 'level_2_ev_charger', label: 'EV Charger' },
];

// Comparison appliances for Section 1 (labelLine1 and labelLine2 for two-line display)
export const comparisonAppliancesList = [
  { key: 'kitchenRange', labelLine1: 'KITCHEN', labelLine2: 'RANGE' },
  { key: 'clothesDryer', labelLine1: 'CLOTHES', labelLine2: 'DRYER' },
  { key: 'airFurnace', labelLine1: 'AIR', labelLine2: 'FURNACE' },
  { key: 'waterHeater', labelLine1: 'WATER', labelLine2: 'HEATER' },
];

function getAppliance(dataset, id) {
  return dataset.appliances.find(a => a.id === id);
}

function calculateFromAppliances(appliances) {
  if (appliances.length === 0) {
    return 0;
  }

  // Sum of running watts
  const totalRunning = appliances.reduce((sum, a) => sum + a.running.avg_w, 0);

  // Find appliance with largest surge delta
  const surgeAppliance = appliances
    .filter(a => a.surge.has_starting_surge && a.surge.delta_avg_w != null)
    .reduce((max, a) => {
      if (!max || a.surge.delta_avg_w > max.surge.delta_avg_w) {
        return a;
      }
      return max;
    }, null);

  // Surge-adjusted watts
  let surgeAdjusted = totalRunning;
  if (surgeAppliance) {
    surgeAdjusted = totalRunning - surgeAppliance.running.avg_w + surgeAppliance.starting.avg_w;
  }

  // Apply margin
  const finalWatts = surgeAdjusted * (1 + MARGIN_PCT / 100);

  // Convert to kW
  return finalWatts / 1000;
}

function calculateForDataset(dataset, applianceIds) {
  const appliances = applianceIds
    .map(id => getAppliance(dataset, id))
    .filter(Boolean);
  return calculateFromAppliances(appliances);
}

export function calculateGeneratorSize(comparisonSelections, electricSelections) {
  // Build list of appliances for user's actual selections
  // Each item specifies which dataset to look it up in
  const userAppliances = [];

  // Add Section 2 electric appliances (same in both datasets, use electric)
  Array.from(electricSelections).forEach(id => {
    userAppliances.push(getAppliance(electricDataset, id));
  });

  // Add Section 1 comparison appliances based on user's fuel choice
  Object.entries(comparisonSelections).forEach(([key, value]) => {
    if (value === 'propane') {
      // User chose propane - look up propane appliance in propane dataset
      userAppliances.push(getAppliance(propaneDataset, comparisonApplianceMap[key].propane));
    } else if (value === 'electric') {
      // User chose electric - look up electric appliance in electric dataset
      userAppliances.push(getAppliance(electricDataset, comparisonApplianceMap[key].electric));
    }
  });

  // Build all-electric comparison list (always from electric dataset)
  const allElectricApplianceIds = [...Array.from(electricSelections)];

  // For all-electric, include all comparison appliances as electric versions
  Object.entries(comparisonSelections).forEach(([key, value]) => {
    if (value !== null) {
      allElectricApplianceIds.push(comparisonApplianceMap[key].electric);
    }
  });

  const userKw = calculateFromAppliances(userAppliances.filter(Boolean));
  const allElectricKw = calculateForDataset(electricDataset, allElectricApplianceIds);

  return {
    propaneKw: Math.round(userKw * 100) / 100,
    allElectricKw: Math.round(allElectricKw * 100) / 100,
  };
}

// Calculate maximum all-electric value (all comparison appliances + all section 2 appliances as electric)
export function calculateMaxAllElectric() {
  const allElectricIds = [
    // All section 2 electric appliances
    ...electricAppliancesList.map(a => a.id),
    // All comparison appliances as electric
    ...Object.values(comparisonApplianceMap).map(m => m.electric),
  ];

  const maxKw = calculateForDataset(electricDataset, allElectricIds);
  return Math.round(maxKw * 100) / 100;
}
